/**
 * DropPanel — toggleable dropdown panels.
 * Opens/closes on trigger click and closes on outside click.
 */
class DropPanel {
    /**
     * @param {Object} options - Configuration object.
     * @param {string} options.container - Selector for the wrapper element.
     * @param {string} options.button - Selector for the trigger button inside wrapper.
     * @param {string} options.openClass - CSS class toggled on the wrapper when open.
     * @param {Function} options.onOpen - Callback fired on open, receives wrapper element.
     * @param {Function} options.onClose - Callback fired on close, receives wrapper element.
     * @param {Function} options.onSelect - Callback fired on item click, receives { item, wrapper }.
     */
    constructor({
        container = '[data-dropdown="wrapper"]',
        button = '[data-dropdown="button"]',
        openClass = 'is-open',
        onOpen = () => {},
        onClose = () => {},
        onSelect = () => {}
    } = {}) {
        this.config = {
            container,
            button,
            openClass,
            onOpen,
            onClose,
            onSelect
        };

        /** @type {Map<HTMLElement, Function>} */
        this.listeners = new Map();
        this.boundOutsideHandler = this.handleClickOutside.bind(this);
    }

    /**
     * Attach click listeners to each wrapper and a global outside-click handler.
     */
    init() {
        const wrappers = document.querySelectorAll(this.config.container);

        if (!wrappers.length) {
            return;
        }

        wrappers.forEach((wrapper) => {
            const button = wrapper.querySelector(this.config.button);

            if (button) {
                button.setAttribute('aria-expanded', 'false');
            }

            const handler = (event) => this.handleClick(event, wrapper);
            wrapper.addEventListener('click', handler);
            this.listeners.set(wrapper, handler);
        });

        document.addEventListener('click', this.boundOutsideHandler);
    }

    /**
     * Remove all attached event listeners.
     */
    destroy() {
        this.listeners.forEach((handler, wrapper) => {
            wrapper.removeEventListener('click', handler);
        });

        this.listeners.clear();
        document.removeEventListener('click', this.boundOutsideHandler);
    }

    /**
     * Handle click inside a wrapper: toggle on button, fire onSelect on item.
     *
     * @param {MouseEvent} event - Click event.
     * @param {HTMLElement} wrapper - Parent wrapper element.
     */
    handleClick(event, wrapper) {
        const trigger = event.target.closest(this.config.button);

        if (trigger && wrapper.contains(trigger)) {
            event.preventDefault();
            this.toggle(wrapper);
            return;
        }

        const item = event.target.closest('[data-dropdown="item"]');

        if (item && wrapper.contains(item)) {
            this.config.onSelect({ item, wrapper });
            this.close(wrapper);
        }
    }

    /**
     * Close any open panel when clicking outside.
     *
     * @param {MouseEvent} event - Click event on document.
     */
    handleClickOutside(event) {
        const openPanels = document.querySelectorAll(
            `${this.config.container}.${this.config.openClass}`
        );

        openPanels.forEach((panel) => {
            if (!panel.contains(event.target)) {
                this.close(panel);
            }
        });
    }

    /**
     * Toggle the panel open/closed state.
     *
     * @param {HTMLElement} wrapper - Wrapper element.
     */
    toggle(wrapper) {
        const isOpen = wrapper.classList.contains(this.config.openClass);

        if (isOpen) {
            this.close(wrapper);
        } else {
            this.open(wrapper);
        }
    }

    /**
     * Open the dropdown panel.
     *
     * @param {HTMLElement} wrapper - Wrapper element.
     */
    open(wrapper) {
        wrapper.classList.add(this.config.openClass);
        const button = wrapper.querySelector(this.config.button);

        if (button) {
            button.setAttribute('aria-expanded', 'true');
        }

        this.config.onOpen(wrapper);
    }

    /**
     * Close the dropdown panel.
     *
     * @param {HTMLElement} wrapper - Wrapper element.
     */
    close(wrapper) {
        wrapper.classList.remove(this.config.openClass);
        const button = wrapper.querySelector(this.config.button);

        if (button) {
            button.setAttribute('aria-expanded', 'false');
        }

        this.config.onClose(wrapper);
    }
}

export { DropPanel };
